import axios from 'axios';

// Backend API Configuration
const getBackendApiUrl = () => {
  const isLocal = window.location.hostname === 'localhost' || 
                  window.location.hostname === '127.0.0.1' || 
                  process.env.NODE_ENV === 'development';
  
  // Use environment variable to determine backend, default to remote in development
  const useLocalBackend = process.env.REACT_APP_USE_LOCAL_BACKEND === 'true';
  
  if (!isLocal) {
    // Production - use nginx proxy
    return '/backend';
  }
  
  // Development - check environment variable
  if (useLocalBackend) {
    return 'http://localhost:8008/api'; // Local backend
  } else {
    return 'http://64.225.20.106:8008/api'; // Remote server directly
  }
};

const BACKEND_API_URL = getBackendApiUrl();

// Create axios instance for backend API
const backendApiClient = axios.create({
  baseURL: BACKEND_API_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log the configuration for debugging
console.log(`[BackendAPI] Configuration - URL: ${BACKEND_API_URL}, Environment: ${process.env.NODE_ENV}, Hostname: ${window.location.hostname}, Version: 2025-08-05`);

// Request interceptor to add auth headers if needed
backendApiClient.interceptors.request.use(
  (config) => {
    const startTime = Date.now();
    config.metadata = { startTime };
    
    const token = localStorage.getItem('access_token');
    console.log(`[BackendAPI] Token check:`, {
      tokenExists: !!token,
      tokenLength: token ? token.length : 0,
      tokenStart: token ? token.substring(0, 20) + '...' : 'N/A',
      storageKeys: Object.keys(localStorage)
    });
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`[BackendAPI] Added Authorization header`);
    } else {
      console.warn(`[BackendAPI] No access token found in localStorage`);
    }
    
    const dataSize = config.data ? JSON.stringify(config.data).length : 0;
    console.log(`[BackendAPI] ${config.method?.toUpperCase()} ${config.url} - Data size: ${dataSize}B`);
    return config;
  },
  (error) => {
    console.error(`[BackendAPI] Request interceptor error:`, error.message);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
backendApiClient.interceptors.response.use(
  (response) => {
    const duration = response.config.metadata ? Date.now() - response.config.metadata.startTime : 0;
    const responseSize = response.data ? JSON.stringify(response.data).length : 0;
    console.log(`[BackendAPI] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status} in ${duration}ms, Response: ${responseSize}B`);
    return response;
  },
  (error) => {
    const duration = error.config?.metadata ? Date.now() - error.config.metadata.startTime : 0;
    const status = error.response?.status || 'Network Error';
    console.error(`[BackendAPI] ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${status} after ${duration}ms:`, error.message);
    
    if (error.response?.status === 401) {
      console.log(`[BackendAPI] Unauthorized access - Token may be invalid or expired`);
      // Don't automatically remove token - let user re-authenticate through Auth0 if needed
      // localStorage.removeItem('access_token');
    } else if (error.response?.status === 429) {
      const retryAfter = error.response.headers['retry-after'];
      const remainingRequests = error.response.headers['x-ratelimit-remaining'];
      const resetTime = error.response.headers['x-ratelimit-reset'];
      
      console.warn(`[BackendAPI] Rate limit exceeded - Retry after: ${retryAfter}s, Remaining: ${remainingRequests}, Reset: ${resetTime}`);
      
      // Add rate limit info to error for components to use
      error.rateLimitInfo = {
        retryAfter: parseInt(retryAfter) || 60,
        remainingRequests: parseInt(remainingRequests) || 0,
        resetTime: resetTime ? new Date(parseInt(resetTime) * 1000) : null
      };
    }
    return Promise.reject(error);
  }
);

/**
 * Get freight data with filters
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} - Array of freight data
 */
export const getFreightData = async (filters = {}) => {
  try {
    const params = {
      pickup_date_begin: filters.pickupDateBegin,
      pickup_date_end: filters.pickupDateEnd,
      equipment_types: filters.equipmentTypes,
      origin_states: filters.originStates,
      destination_states: filters.destinationStates,
      min_payment: filters.minPayment || 0,
      min_rate_per_mile: filters.minRatePerMile || 0,
      limit: filters.limit || 1000
    };

    // Remove undefined values
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null) {
        delete params[key];
      }
    });

    const response = await backendApiClient.get('/freight-data', { params });
    
    if (response.status === 200 && response.data.data) {
      return response.data.data;
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error) {
    console.error('Error fetching freight data:', error);
    throw error;
  }
};

/**
 * Get broker metrics
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} - Broker metrics data
 */
export const getBrokerMetrics = async (filters = {}) => {
  try {
    const filterData = {
      pickup_date_begin: filters.pickupDateBegin,
      pickup_date_end: filters.pickupDateEnd,
      equipment_types: filters.equipmentTypes,
      load_types: filters.loadTypes,
      origin_states: filters.originStates,
      destination_states: filters.destinationStates,
      min_payment: filters.minPayment || 0,
      min_rate_per_mile: filters.minRatePerMile || 0,
      max_length: filters.maxLength || 75,
      max_weight: filters.maxWeight || 100000,
      limit: filters.limit || 1000
    };

    // Remove undefined values
    Object.keys(filterData).forEach(key => {
      if (filterData[key] === undefined || filterData[key] === null) {
        delete filterData[key];
      }
    });

    const response = await backendApiClient.post('/broker-metrics', filterData);
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error) {
    console.error('Error fetching broker metrics:', error);
    throw error;
  }
};

/**
 * Get rate insights
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} - Rate insights data
 */
export const getRateInsights = async (filters = {}) => {
  try {
    const filterData = {
      pickup_date_begin: filters.pickupDateBegin,
      pickup_date_end: filters.pickupDateEnd,
      equipment_types: filters.equipmentTypes,
      load_types: filters.loadTypes,
      origin_states: filters.originStates,
      destination_states: filters.destinationStates,
      min_payment: filters.minPayment || 0,
      min_rate_per_mile: filters.minRatePerMile || 0,
      max_length: filters.maxLength || 75,
      max_weight: filters.maxWeight || 100000,
      limit: filters.limit || 1000,
      include_intra_state: filters.includeIntraState
    };

    // Remove undefined values
    Object.keys(filterData).forEach(key => {
      if (filterData[key] === undefined || filterData[key] === null) {
        delete filterData[key];
      }
    });

    const response = await backendApiClient.post('/rate-insights', filterData);
    
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error) {
    console.error('Error fetching rate insights:', error);
    throw error;
  }
};

/**
 * Get city details with load listings and statistics
 * @param {Object} cityParams - City parameters
 * @returns {Promise<Object>} - City details data
 */
export const getCityDetails = async (cityParams) => {
  const requestData = {
    city: cityParams.city,
    state: cityParams.state,
    pickup_date_begin: cityParams.pickupDateBegin,
    pickup_date_end: cityParams.pickupDateEnd,
    equipment_types: cityParams.equipmentTypes,
    load_types: cityParams.loadTypes,
    min_payment: cityParams.minPayment,
    min_rate_per_mile: cityParams.minRatePerMile,
    include_intra_state: cityParams.includeIntraState,
    include_loads: cityParams.includeLoads,
    limit: cityParams.limit
  };

  // Remove undefined values
  Object.keys(requestData).forEach(key => {
    if (requestData[key] === undefined || requestData[key] === null) {
      delete requestData[key];
    }
  });

  try {
    console.log('[getCityDetails] Sending request with data:', requestData);
    const response = await backendApiClient.post('/city-details', requestData);

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error) {
    console.error('Error fetching city details:', error);
    console.error('Request data was:', requestData);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    throw error;
  }
};

/**
 * Get lane pricing
 * @param {Object} laneData - Lane parameters
 * @returns {Promise<Object>} - Lane pricing data
 */
export const getLanePricing = async (laneData) => {
  try {
    const requestData = {
      origin_city: laneData.originCity,
      origin_state: laneData.originState,
      destination_city: laneData.destinationCity,
      destination_state: laneData.destinationState,
      equipment_type: laneData.equipmentType
    };

    const response = await backendApiClient.post('/lane-pricing', requestData);
    
    if (response.status === 200) {
      // Check if the response contains an error message
      if (response.data.error) {
        throw new Error(response.data.error);
      }
      return response.data;
    } else {
      throw new Error(`API returned status ${response.status}`);
    }
  } catch (error) {
    console.error('Error fetching lane pricing:', error);
    throw error;
  }
};

/**
 * Test backend API connectivity
 * @returns {Promise<Object>} - Object with connectivity status and rate limit info
 */
export const testBackendApiConnectivity = async () => {
  const startTime = Date.now();
  try {
    console.log(`[BackendAPI] Testing connectivity to: ${BACKEND_API_URL}`);
    // Use freight-data endpoint with minimal params since health endpoint isn't available through nginx proxy
    const response = await backendApiClient.get('/freight-data', { 
      timeout: 30000,  // 30 seconds to match Heat Map timeout requirements
      params: { limit: 1 }
    });
    const duration = Date.now() - startTime;
    console.log(`[BackendAPI] Connectivity test PASSED in ${duration}ms - Status: ${response.status}`);
    
    // Extract rate limit headers if available
    const rateLimitInfo = {
      remaining: response.headers['x-ratelimit-remaining'] ? parseInt(response.headers['x-ratelimit-remaining']) : null,
      limit: response.headers['x-ratelimit-limit'] ? parseInt(response.headers['x-ratelimit-limit']) : null,
      resetTime: response.headers['x-ratelimit-reset'] ? new Date(parseInt(response.headers['x-ratelimit-reset']) * 1000) : null
    };
    
    return { 
      isOnline: true, 
      rateLimitInfo: rateLimitInfo.remaining !== null ? rateLimitInfo : null 
    };
  } catch (error) {
    const duration = Date.now() - startTime;
    const status = error.response?.status || 'No Response';
    
    // Treat any HTTP response (including 401, 403, etc.) as "online" since the server is responding
    if (error.response && error.response.status) {
      console.log(`[BackendAPI] Backend is online but returned ${status} - treating as connected`);
      
      // Extract rate limit info if available
      const rateLimitInfo = error.response.headers ? {
        remaining: error.response.headers['x-ratelimit-remaining'] ? parseInt(error.response.headers['x-ratelimit-remaining']) : null,
        limit: error.response.headers['x-ratelimit-limit'] ? parseInt(error.response.headers['x-ratelimit-limit']) : null,
        resetTime: error.response.headers['x-ratelimit-reset'] ? new Date(parseInt(error.response.headers['x-ratelimit-reset']) * 1000) : null
      } : null;
      
      return { 
        isOnline: true, 
        rateLimitInfo: rateLimitInfo && rateLimitInfo.remaining !== null ? rateLimitInfo : null 
      };
    }
    
    // Only treat as offline if there's no response at all (network error, timeout, etc.)
    console.warn(`[BackendAPI] Connectivity test FAILED after ${duration}ms - No response from server: ${error.message}`);
    return { 
      isOnline: false, 
      rateLimitInfo: null 
    };
  }
};

export default backendApiClient;